import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Layers,
  Building2,
  TrendingUp,
  Globe,
  BarChart3,
  ChevronRight,
} from 'lucide-react';
import { apiClient } from '../lib/api/config.js';
import { format } from 'date-fns';

interface SynthesisArticle {
  article_id: string;
  article_title: string;
  word_count: number;
  publish_status: string;
  generation_timestamp: string;
  voice_name: string | null;
  persona_name: string | null;
  synthesis_thread_id: string | null;
  thread_type: string | null;
  thread_display_name: string | null;
  member_count: number;
  entity_id?: string | null;
  synthesis_kind?: string | null;
}

const TYPE_ICONS: Record<string, typeof Building2> = {
  entity: Building2,
  activity: TrendingUp,
  sector: Globe,
  macro: BarChart3,
};

const TYPE_COLORS: Record<string, string> = {
  entity: 'bg-primary-100 text-primary-700',
  activity: 'bg-warning-100 text-warning-700',
  sector: 'bg-success-100 text-success-700',
  macro: 'bg-purple-100 text-purple-700',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-grey-100 text-grey-600',
  approved: 'bg-primary-100 text-primary-700',
  published: 'bg-success-100 text-success-700',
  archived: 'bg-grey-100 text-grey-400',
};

// Tab definitions: covers synthesis_lanes kinds + synthesis_generator
// thread types. `all` is the catch-all.
type KindFilter =
  | 'all'
  | 'pre_market_brief'
  | 'market_open_report'
  | 'close_wrap'
  | 'sector_wrap'
  | 'weekly_theme'
  | 'director_trade_roundup'
  | 'spike_explainer'
  | 'entity'
  | 'activity'
  | 'sector'
  | 'macro';

const KIND_TABS: { value: KindFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pre_market_brief', label: 'Pre-Market' },
  { value: 'market_open_report', label: 'Market-Open' },
  { value: 'close_wrap', label: 'Close Wrap' },
  { value: 'sector_wrap', label: 'Sector Wrap' },
  { value: 'weekly_theme', label: 'Weekly Theme' },
  { value: 'director_trade_roundup', label: 'Director Trades' },
  { value: 'spike_explainer', label: 'Spike Explainer' },
  { value: 'entity', label: 'Entity Narrative' },
  { value: 'activity', label: 'Activity Roundup' },
  { value: 'sector', label: 'Sector Intelligence' },
  { value: 'macro', label: 'Macro' },
];

async function fetchSynthesisArticles(
  page: number,
  limit: number,
  kind: KindFilter,
) {
  const params: Record<string, string | number> = { page, limit };
  if (kind !== 'all') params.synthesis_kind = kind;
  const response = await apiClient.get('/articles/synthesis', { params });
  return response.data;
}

export function SynthesisPage() {
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState<KindFilter>('all');
  const limit = 25;

  const { data, isLoading } = useQuery({
    queryKey: ['synthesis-articles', page, kind],
    queryFn: () => fetchSynthesisArticles(page, limit, kind),
  });

  const articles: SynthesisArticle[] = data?.items || [];
  const total = data?.total || 0;
  const pages = data?.pages || 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-grey-900">Synthesis Articles</h1>
        <p className="mt-2 text-grey-500">
          AI-generated synthesis content — daily briefs, sector wraps, spike explainers, story-thread narratives.
        </p>
      </div>

      {/* Synthesis-kind tabs */}
      <div className="border-b border-grey-200 mb-6 overflow-x-auto">
        <nav className="-mb-px flex gap-4 whitespace-nowrap" aria-label="Synthesis kind">
          {KIND_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => { setKind(tab.value); setPage(1); }}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                kind === tab.value
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="bg-surface rounded-xl border border-grey-200 shadow-md overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-grey-500">Loading synthesis articles...</div>
        ) : articles.length === 0 ? (
          <div className="p-16 text-center">
            <Layers className="h-16 w-16 text-grey-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-grey-900 mb-2">No Synthesis Articles Yet</h2>
            <p className="text-grey-500">
              {kind === 'all'
                ? <>Queue story threads for synthesis from the{' '}
                    <Link to="/threads" className="text-primary-600 hover:text-primary-700">Story Threads</Link>{' '}page</>
                : <>No articles for this lane yet. Try the <button onClick={() => setKind('all')} className="text-primary-600 hover:text-primary-700">All</button> tab.</>}
            </p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-grey-100">
              {articles.map((article) => {
                const lookupKey = article.synthesis_kind || article.thread_type || '';
                const Icon = TYPE_ICONS[lookupKey] || Layers;
                const colorClass = TYPE_COLORS[lookupKey] || 'bg-grey-100 text-grey-600';
                return (
                  <Link
                    key={article.article_id}
                    to={`/articles/${article.article_id}`}
                    className="flex items-center gap-3 sm:gap-4 px-3 sm:px-6 py-4 hover:bg-grey-50 transition-colors"
                  >
                    <div className={`p-2 rounded-lg ${colorClass}`}>
                      <Icon className="h-5 w-5" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-grey-900 text-sm line-clamp-1">
                        {article.article_title}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        {article.synthesis_kind && (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${colorClass}`}>
                            {article.synthesis_kind.replace(/_/g, ' ')}
                          </span>
                        )}
                        {!article.synthesis_kind && article.thread_type && (
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium capitalize ${TYPE_COLORS[article.thread_type] || 'bg-grey-100'}`}>
                            {article.thread_type}
                          </span>
                        )}
                        {article.thread_display_name && (
                          <span className="text-xs text-grey-500 truncate max-w-[200px]">
                            {article.thread_display_name}
                          </span>
                        )}
                        <span className="text-xs text-grey-400">{article.word_count} words</span>
                        <span className="text-xs text-grey-400">
                          {format(new Date(article.generation_timestamp), 'MMM d, yyyy')}
                        </span>
                      </div>
                    </div>

                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[article.publish_status] || 'bg-grey-100'}`}>
                      {article.publish_status}
                    </span>

                    <ChevronRight className="h-4 w-4 text-grey-400 flex-shrink-0" />
                  </Link>
                );
              })}
            </div>

            {pages > 1 && (
              <div className="px-6 py-3 bg-grey-50 border-t border-grey-200 flex items-center justify-between">
                <p className="text-sm text-grey-500">
                  Page {page} of {pages} ({total} articles)
                </p>
                <div className="space-x-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-grey-300 rounded text-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= pages}
                    className="px-3 py-1 border border-grey-300 rounded text-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
